///
/// EarthBrowser
///
/// Created by Matt Giger on October 10, 2026
/// Copyright (c) 2026 Geodata Labs
///
/// Insights: the six cards from ANALYTICS_STRATEGY.md §6, composed
/// client-side from metrics_daily rows (one request). Deliberately dumb —
/// all definitions live in the rollup — so a metric change is a server
/// change. Cards: traffic & entry, activation funnel, engagement & layers,
/// authoring & spread, retention, quality.
///

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/components/ui/use-toast'
import { useGetInsightsQuery, useRunInsightsRollupMutation, type MetricRow } from '@/redux/api/insightsApi'
import { Activity, BookOpen, Gauge, Layers, RefreshCw, Repeat, Users } from 'lucide-react'
import { useMemo, useState } from 'react'

// ---------- helpers over the flat metrics_daily payload ----------

type Dims = Record<string, string>

function dimsKey(dims: Dims): string {
  return JSON.stringify(dims)
}

class Metrics {
  private byMetric = new Map<string, MetricRow[]>()
  readonly days: string[]

  constructor(rows: MetricRow[], days: number) {
    for (const row of rows) {
      const list = this.byMetric.get(row.metric) ?? []
      list.push(row)
      this.byMetric.set(row.metric, list)
    }
    this.days = []
    for (let i = days - 1; i >= 0; i--) {
      this.days.push(new Date(Date.now() - (i + 1) * 86400_000).toISOString().slice(0, 10))
    }
  }

  /** Daily series for metric+dims, zero-filled over the window */
  series(metric: string, dims: Dims = {}): number[] {
    const key = dimsKey(dims)
    const byDay = new Map<string, number>()
    for (const row of this.byMetric.get(metric) ?? []) {
      if (dimsKey(row.dims) === key) byDay.set(row.day.slice(0, 10), row.value)
    }
    return this.days.map(d => byDay.get(d) ?? 0)
  }

  sum(metric: string, dims: Dims = {}, lastN?: number): number {
    const s = this.series(metric, dims)
    return (lastN ? s.slice(-lastN) : s).reduce((a, b) => a + b, 0)
  }

  /** Average over days that have a value (medians/rates, not counts) */
  avg(metric: string, dims: Dims = {}, lastN?: number): number | null {
    const key = dimsKey(dims)
    const cutoff = lastN ? this.days.slice(-lastN)[0] : this.days[0]
    const vals = (this.byMetric.get(metric) ?? [])
      .filter(r => dimsKey(r.dims) === key && r.day.slice(0, 10) >= cutoff)
      .map(r => r.value)
    if (vals.length === 0) return null
    return vals.reduce((a, b) => a + b, 0) / vals.length
  }

  latest(metric: string, dims: Dims = {}): number | null {
    const rows = (this.byMetric.get(metric) ?? []).filter(r => dimsKey(r.dims) === dimsKey(dims))
    if (rows.length === 0) return null
    return rows[rows.length - 1].value
  }

  /** Top-N breakdown of a dimensioned metric summed over the window */
  top(metric: string, dim: string, n = 8, lastN?: number): { key: string; value: number }[] {
    const cutoff = lastN ? this.days.slice(-lastN)[0] : this.days[0]
    const totals = new Map<string, number>()
    for (const row of this.byMetric.get(metric) ?? []) {
      const k = row.dims[dim]
      if (!k || row.day.slice(0, 10) < cutoff) continue
      totals.set(k, (totals.get(k) ?? 0) + row.value)
    }
    return [...totals.entries()].map(([key, value]) => ({ key, value }))
      .sort((a, b) => b.value - a.value).slice(0, n)
  }
}

function fmt(n: number | null | undefined, digits = 0): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—'
  if (Math.abs(n) >= 10_000) return `${(n / 1000).toFixed(1)}k`
  return n.toLocaleString(undefined, { maximumFractionDigits: digits })
}

function pct(n: number | null | undefined): string {
  return n === null || n === undefined ? '—' : `${Math.round(n * 100)}%`
}

function minutes(seconds: number | null): string {
  return seconds === null ? '—' : `${(seconds / 60).toFixed(1)}m`
}

// ---------- presentational pieces (design-system card/tile patterns) ----------

function Sparkline({ values, className = 'text-blue-500' }: { values: number[]; className?: string }) {
  const max = Math.max(1, ...values)
  return (
    <div className="flex items-end gap-px h-8" aria-hidden>
      {values.map((v, i) => (
        <div
          key={i}
          className={`flex-1 rounded-sm bg-current opacity-70 ${className}`}
          style={{ height: `${Math.max(2, (v / max) * 100)}%` }}
        />
      ))}
    </div>
  )
}

function Bars({ items, unit = '' }: { items: { key: string; value: number }[]; unit?: string }) {
  const max = Math.max(1, ...items.map(i => i.value))
  if (items.length === 0) return <p className="text-xs text-muted-foreground">No data yet</p>
  return (
    <div className="space-y-1.5">
      {items.map(item => (
        <div key={item.key} className="flex items-center gap-2 text-xs">
          <span className="w-28 truncate font-mono">{item.key}</span>
          <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
            <div className="h-full rounded-full bg-blue-500" style={{ width: `${(item.value / max) * 100}%` }} />
          </div>
          <span className="w-14 text-right tabular-nums">{fmt(item.value)}{unit}</span>
        </div>
      ))}
    </div>
  )
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="min-w-0">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold leading-tight tabular-nums">{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground truncate">{sub}</p>}
    </div>
  )
}

function Card({ icon, title, description, children }: {
  icon: React.ReactNode
  title: string
  description: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400">{icon}</div>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold">{title}</h3>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
      </div>
      {children}
    </div>
  )
}

function FunnelStep({ label, value, base }: { label: string; value: number; base: number }) {
  const fraction = base > 0 ? value / base : 0
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-32 truncate">{label}</span>
      <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
        <div className="h-full rounded-full bg-emerald-500" style={{ width: `${Math.round(fraction * 100)}%` }} />
      </div>
      <span className="w-20 text-right tabular-nums">{fmt(value)} · {pct(fraction)}</span>
    </div>
  )
}

// ---------- section ----------

const cWindows = [7, 28, 90] as const

export function InsightsSection() {
  const [days, setDays] = useState<(typeof cWindows)[number]>(28)
  const { data, isLoading, isError } = useGetInsightsQuery({ days })
  const [runRollup, { isLoading: rolling }] = useRunInsightsRollupMutation()
  const { toast } = useToast()

  const m = useMemo(() => (data ? new Metrics(data.rows, data.days) : null), [data])

  const handleRollup = async () => {
    try {
      const result = await runRollup({}).unwrap()
      toast({ title: 'Rollup complete', description: `${result.written} metric rows written for ${result.day}` })
    } catch {
      toast({ title: 'Rollup failed', variant: 'destructive' })
    }
  }

  if (isLoading || !m || !data) {
    return (
      <div className="grid gap-3 lg:grid-cols-2">
        {[0, 1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-56 rounded-xl" />)}
      </div>
    )
  }
  if (isError) {
    return (
      <div className="rounded-xl border border-red-500/70 bg-red-500/10 p-6 text-center text-sm">
        Could not load insights
      </div>
    )
  }

  // Funnel bases (window totals)
  const sessions = m.sum('sessions')
  const ready = m.sum('event_count', { event: 'app_ready' })
  const interacted = m.sum('event_count', { event: 'first_interaction' })
  const firstLayer = m.sum('sessions_with_layer')
  const activated = m.sum('activated_sessions')

  const chipClicks = m.sum('event_count', { event: 'register_chip_clicked' })
  const authOpened = m.sum('event_count', { event: 'auth_dialog_opened' })
  const accounts = m.sum('accounts_created')

  const opens = m.top('artifact_opens', 'kind', 6)
  const opensNonOwner = m.top('artifact_opens_nonowner', 'kind', 6)
  const artifactsTotal = m.top('artifacts_created', 'kind', 6).reduce((s, i) => s + i.value, 0)
  const crashesTotal = m.sum('crashes')
  const crashLoops = m.sum('crashes', { level: '2' }) + m.sum('crashes', { level: '3' })
  // One bar list for both gate answers: "radar" blocked vs "radar (lite)" let through
  const memoryGate = [
    ...m.top('memory_blocks', 'layer', 4),
    ...m.top('memory_lite_enables', 'layer', 4).map(i => ({ key: `${i.key} (lite)`, value: i.value })),
  ].sort((a, b) => b.value - a.value).slice(0, 6)

  return (
    <div className="space-y-4">
      {/* Header strip: live + window + rollup */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-4">
        <div className="flex flex-wrap gap-6">
          <Stat label="Sessions today" value={fmt(data.live.sessions_today)} sub="live, from raw events" />
          <Stat label="Visitors today" value={fmt(data.live.dau_today)} sub="anon daily touch" />
          <Stat label="Events today" value={fmt(data.live.events_today)} sub={`${fmt(data.live.events_total)} retained (90d)`} />
          <Stat
            label="Last rollup"
            value={data.lastRollup.day ?? '—'}
            sub={data.lastRollup.computed_at ? new Date(data.lastRollup.computed_at).toLocaleString() : 'never'}
          />
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-border overflow-hidden">
            {cWindows.map(w => (
              <button
                key={w}
                onClick={() => setDays(w)}
                aria-pressed={days === w}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  days === w ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:bg-accent/50'
                }`}
              >
                {w}d
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={handleRollup} disabled={rolling} aria-label="Run yesterday's rollup">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${rolling ? 'animate-spin' : ''}`} />
            Roll up
          </Button>
        </div>
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        {/* 1. Traffic & entry */}
        <Card icon={<Users className="h-4 w-4" />} title="Traffic & entry" description="Sessions per day and how visitors arrived">
          <div className="grid grid-cols-3 gap-3">
            <Stat label={`Sessions (${days}d)`} value={fmt(sessions)} />
            <Stat label="Mobile share" value={pct(sessions > 0 ? m.sum('sessions', { device: 'mobile' }) / sessions : null)} />
            <Stat label="Avg visitors/day" value={fmt(m.avg('dau'))} />
          </div>
          <Sparkline values={m.series('sessions')} />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Entry</p>
              <Bars items={m.top('sessions', 'entry', 6)} />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Country</p>
              <Bars items={m.top('sessions', 'country', 6)} />
            </div>
          </div>
        </Card>

        {/* 2. Activation funnel */}
        <Card icon={<Activity className="h-4 w-4" />} title="Activation" description="How far new sessions get, and the sign-up funnel">
          <div className="space-y-1.5">
            <FunnelStep label="Launched" value={sessions} base={sessions} />
            <FunnelStep label="First frame" value={ready} base={sessions} />
            <FunnelStep label="Interacted" value={interacted} base={sessions} />
            <FunnelStep label="Chose a layer" value={firstLayer} base={sessions} />
            <FunnelStep label="Activated (2 layers, 60s)" value={activated} base={sessions} />
          </div>
          <div className="grid grid-cols-3 gap-3 pt-1">
            <Stat label="Cold start p75" value={m.avg('first_frame_ms_p75') === null ? '—' : `${(m.avg('first_frame_ms_p75')! / 1000).toFixed(1)}s`} />
            <Stat label="First layer (median)" value={m.avg('first_layer_s_p50') === null ? '—' : `${fmt(m.avg('first_layer_s_p50'))}s`} />
            <Stat label="Onboarding done" value={pct(
              m.sum('event_count', { event: 'onboarding_started' }) > 0
                ? m.sum('event_count', { event: 'onboarding_completed' }) / m.sum('event_count', { event: 'onboarding_started' })
                : null)} />
          </div>
          <div className="space-y-1.5 pt-1">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Sign-up funnel</p>
            <FunnelStep label="Register chip" value={chipClicks} base={sessions} />
            <FunnelStep label="Auth dialog" value={authOpened} base={sessions} />
            <FunnelStep label="Accounts created" value={accounts} base={sessions} />
          </div>
        </Card>

        {/* 3. Engagement & layers */}
        <Card icon={<Layers className="h-4 w-4" />} title="Engagement" description="Active time and which layers people actually use">
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Median active" value={minutes(m.avg('active_s_p50'))} sub={`mobile ${minutes(m.avg('active_s_p50', { device: 'mobile' }))}`} />
            <Stat label="Time control" value={pct(sessions > 0 ? m.sum('time_control_sessions') / sessions : null)} sub="of sessions" />
            <Stat label="Files imported" value={fmt(m.top('files_imported', 'format', 50).reduce((s, i) => s + i.value, 0))} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Layer sessions</p>
              <Bars items={m.top('layer_sessions', 'layer', 8)} />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Layer dwell (hours)</p>
              <Bars items={m.top('layer_dwell_s', 'layer', 8).map(i => ({ key: i.key, value: Math.round(i.value / 360) / 10 }))} />
            </div>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Windows opened</p>
            <Bars items={m.top('windows_opened', 'window_type', 6)} />
          </div>
        </Card>

        {/* 4. Authoring & spread */}
        <Card icon={<BookOpen className="h-4 w-4" />} title="Authoring & spread" description="Artifacts made, and opened by people who did not make them">
          <div className="grid grid-cols-3 gap-3">
            <Stat label={`Artifacts (${days}d)`} value={fmt(artifactsTotal)} />
            <Stat label="Repeat authors (28d)" value={fmt(m.latest('repeat_authors_28d'))} sub={`${fmt(m.latest('authors_28d'))} authors`} />
            <Stat label="Non-owner opens" value={fmt(m.sum('artifact_opens_nonowner'))} sub={`${fmt(opens.reduce((s, i) => s + i.value, 0))} total opens`} />
          </div>
          <Sparkline values={m.series('artifact_opens_nonowner')} className="text-emerald-500" />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Created by kind</p>
              <Bars items={m.top('artifacts_created', 'kind', 6)} />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Opened by non-owners</p>
              <Bars items={opensNonOwner} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Stories played" value={fmt(m.sum('stories_played'))} />
            <Stat label="Story completion" value={pct(m.sum('stories_played') > 0 ? m.sum('stories_completed') / m.sum('stories_played') : null)} />
            <Stat label="Embed domain-days" value={fmt(m.sum('embed_domains_active'))} />
          </div>
        </Card>

        {/* 5. Retention */}
        <Card icon={<Repeat className="h-4 w-4" />} title="Retention" description="All visitors (hashed anonymous id), not just accounts">
          <div className="grid grid-cols-3 gap-3">
            <Stat label="WAU" value={fmt(m.latest('wau'))} sub={`MAU ${fmt(m.latest('mau'))}`} />
            <Stat label="Stickiness" value={pct(
              m.latest('mau') && m.latest('mau')! > 0 ? (m.avg('dau', {}, 7) ?? 0) / m.latest('mau')! : null)} sub="DAU ÷ MAU" />
            <Stat label="New visitors/day" value={fmt(m.avg('new_visitors'))} />
          </div>
          <Sparkline values={m.series('dau')} className="text-violet-500" />
          <div className="grid grid-cols-3 gap-3">
            <Stat label="D1 return" value={pct(m.avg('return_d1'))} sub={`n=${fmt(m.sum('return_d1_cohort'))}`} />
            <Stat label="D7 return" value={pct(m.avg('return_d7'))} sub={`n=${fmt(m.sum('return_d7_cohort'))}`} />
            <Stat label="D30 return" value={pct(m.avg('return_d30'))} sub={`n=${fmt(m.sum('return_d30_cohort'))}`} />
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Visitors by entry</p>
            <Bars items={m.top('dau', 'entry', 6)} />
          </div>
        </Card>

        {/* 6. Quality */}
        <Card icon={<Gauge className="h-4 w-4" />} title="Quality" description="Frame rate, crashes and crash recovery, memory gate answers">
          <div className="grid grid-cols-3 gap-3">
            <Stat label="FPS p50 mobile" value={fmt(m.avg('fps_p50', { device: 'mobile' }))} sub={`p10 ${fmt(m.avg('fps_p10', { device: 'mobile' }))}`} />
            <Stat label="FPS p50 desktop" value={fmt(m.avg('fps_p50', { device: 'desktop' }))} sub={`p10 ${fmt(m.avg('fps_p10', { device: 'desktop' }))}`} />
            <Stat label="Crashes / 1k sessions" value={fmt(sessions > 0 ? (m.sum('crashes') / sessions) * 1000 : null, 1)} sub={`${fmt(m.sum('crashes'))} total`} />
          </div>
          <Sparkline values={m.series('crashes')} className="text-rose-500" />
          {/* Crash loops: level 1 is one crash (force-quits included); 2+ means
              the device crashed again within the 30-minute window. Browser
              reports are Chromium/Firefox ground truth, independent of the
              sentinel's inference */}
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Crash loops" value={fmt(crashLoops)} sub={`${pct(crashesTotal > 0 ? crashLoops / crashesTotal : null)} of crashes reached level 2+`} />
            <Stat label="Context-loss reloads" value={fmt(m.sum('crashes', { reason: 'webgl-context-lost' }))} sub={`${fmt(m.sum('crashes', { reason: 'silent' }))} silent kills`} />
            <Stat label="Browser-reported" value={fmt(m.sum('browser_crashes'))} sub={`${fmt(m.sum('browser_crashes', { reason: 'oom' }))} OOM · ${fmt(m.sum('browser_crashes', { reason: 'unresponsive' }))} hung`} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Crash suspects by layer</p>
              <Bars items={m.top('crash_suspects', 'layer', 6)} />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Recovery outcomes</p>
              <Bars items={m.top('crash_recoveries', 'outcome', 6)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Memory gate: blocked / lite by layer</p>
              <Bars items={memoryGate} />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Cold start p75 (s)</p>
              <Bars items={['mobile', 'tablet', 'desktop'].map(d => ({
                key: d, value: Math.round(((m.avg('first_frame_ms_p75', { device: d }) ?? 0) / 100)) / 10,
              })).filter(i => i.value > 0)} unit="s" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Stat label="GPU peak p90 mobile" value={`${fmt(m.avg('gpu_peak_mb_p90', { device: 'mobile' }))} MB`} />
            <Stat label="Long frames/min" value={fmt(m.avg('long_frames_per_min', { device: 'mobile' }), 1)} sub="mobile, sampled" />
            <Stat label="Import failures" value={fmt(m.top('files_failed', 'format', 50).reduce((s, i) => s + i.value, 0))} />
          </div>
        </Card>
      </div>
    </div>
  )
}
